import { inject, Injectable } from '@angular/core';
import { ADDRESS_SUB_TYPE_OPTIONS, ADDRESS_TYPE, ADDRESS_TYPE_OPTIONS, AddressSubType, AddressType } from '@core/address';
import { COMMON_VALIDATION, EMAIL } from '@core/data-access';
import { PortalFieldConfig, uppercaseParser } from '@portal/form/data-access';
import { tap } from 'rxjs';
import { AddressModelKeys, BusinessEntityModelKeys, PersonnelAddressModelKeys } from '../const/form-field-key.const';
import { AddressFormConfigService } from './address-form-config.service';
import { MobileFormConfigService } from './mobile-form-config.service';

@Injectable({ providedIn: 'root' })
export class BusinessEntityFormFieldConfigService {
  private readonly mobileFormConfigService = inject(MobileFormConfigService);
  private readonly addressFormConfigService = inject(AddressFormConfigService);

  getBusinessEntityFieldConfig(): PortalFieldConfig[] {
    return [
      {
        template: '<div class="portal-paragraph">Help us update information regarding your business</div>',
      },
      {
        key: BusinessEntityModelKeys.registeredAddress,
        className: 'field-columns',
        type: 'input',
        defaultValue: null,
        props: {
          label: 'Business Registered Address',
          placeholder: 'Please enter input',
          disabled: true,
          maxLength: COMMON_VALIDATION.MAX_LENGTH,
        },
        expressions: {
          'props.disabled': 'formState.isMyInfo',
          hide: model => !(model.options?.formState?.isMyInfo && model.formControl?.value),
        },
      },
      {
        key: BusinessEntityModelKeys.address,
        resetOnHide: false,
        fieldGroup: [
          {
            key: PersonnelAddressModelKeys.residential,
            fieldGroup: this.initAddressFormFields(AddressType.RESIDENTIAL_ADDRESS),
            fieldGroupClassName: 'field-columns',
            expressions: {
              'props.disabled': 'formState.isMyInfo',
              hide: model => {
                const hasRegisteredAddress = model?.parent?.parent?.model?.[BusinessEntityModelKeys.registeredAddress];
                return model.options?.formState?.isMyInfo && hasRegisteredAddress;
              },
            },
          },
          {
            key: PersonnelAddressModelKeys.differentResidential,
            type: 'checkbox-boolean',
            defaultValue: false,
            props: {
              label: ADDRESS_TYPE.MAILING_ADDRESS,
              labelCheckbox: 'My Mailing Address is different from Residential Address',
            },
            // expressions: { 'props.disabled': 'formState.isMyInfo' },
          },
          {
            key: PersonnelAddressModelKeys.mailing,
            fieldGroup: this.initAddressFormFields(AddressType.MAILING_ADDRESS),
            fieldGroupClassName: 'field-columns',
            expressions: { hide: model => !model?.parent?.model?.[PersonnelAddressModelKeys.differentResidential] },
          },
        ],
      },
      {
        fieldGroupClassName: 'field-columns',
        fieldGroup: [
          {
            fieldGroup: this.mobileFormConfigService.initMobileFieldConfig({
              label: 'Mobile Number',
              key: {
                areaCode: BusinessEntityModelKeys.officeContactAreaCode,
                number: BusinessEntityModelKeys.officeContactNumber,
              },
            }),
          },
          {
            key: BusinessEntityModelKeys.businessActivity,
            type: '#businessActivity',
            props: { required: true },
          },
          // {
          //   key: BusinessEntityModelKeys.businessClassification,
          //   type: 'mat-select',
          //   props: {
          //     label: 'Business Classification',
          //     required: true,
          //     options: initBusinessClassificationOptions(),
          //   },
          //   expressions: {
          //   },
          //   hooks: {
          //     onInit: field => {
          //       return field?.formControl?.valueChanges.pipe(
          //         startWith(field?.formControl?.value),
          //         distinctUntilChanged(),
          //         tap(value => {
          //           const isSoleProprietor = BusinessClassificationEntries?.[value] === BusinessClassificationEntries.SOLE_PROPRIETORSHIP;
          //           this.applicationStore.soleProprietorship$.next(isSoleProprietor);
          //         }),
          //       );
          //     },
          //   },
          // },
          {
            key: BusinessEntityModelKeys.email,
            type: 'input',
            props: {
              label: 'Email',
              required: true,
              placeholder: 'eg. roland.tey@gmail.com',
              maxLength: EMAIL.MAX_LENGTH,
            },
            validators: {
              validation: [{ name: 'email-custom', options: { keyName: BusinessEntityModelKeys.email } }],
            },
            // parsers: [lowercaseParser],
          },
          {
            key: BusinessEntityModelKeys.gstRegistered,
            type: '#yesNo',
            className: 'col-start-1',
            props: {
              label: 'GST Registered',
              required: true,
            },
          },
          {
            key: BusinessEntityModelKeys.propertyCorporation,
            type: '#yesNo',
            props: {
              label: 'Is your company a property corporation?',
              required: true,
              tooltip:
                'Is your Company a Property Corporation?' +
                'More than 50% of the total turnover is derived from property related activities' +
                'More than 50% of the total assets of the Company comprises interests in or rights over immovable property situated in Singapore, excluding immovable property or any part thereof which is used*:' +
                'As premises for any business carried out by the Company' +
                'For the business of a hotel or hostel or' +
                'For community, charity or educational purposes',
            },
          },
          {
            key: BusinessEntityModelKeys.companyProfitable,
            type: '#yesNo',
            props: {
              label: 'Is your company profitable?',
              tooltip: 'Is your Company profitable? ' + 'Have a positive net worth ' + 'Positive EBITA',
              required: true,
            },
          },
          {
            fieldGroup: [
              {
                key: BusinessEntityModelKeys.usCorporation,
                type: '#yesNo',
                props: {
                  label: 'Is your company a US corporation, partnership, estate or trust?',
                  required: true,
                  alwaysShowHint: true,
                },
                expressions: {
                  'props.hint': ({ model }) =>
                    model?.[BusinessEntityModelKeys.usCorporation] ? 'Please also complete the W-9 form' : null,
                },
                hooks: {
                  onInit: field =>
                    field?.formControl?.valueChanges.pipe(
                      tap(value => value === false && field?.form?.get(BusinessEntityModelKeys.tin)?.setValue(null, { onlySelf: true })),
                    ),
                },
              },
              {
                key: BusinessEntityModelKeys.tin,
                type: 'input',
                validators: { validation: ['tin'] },
                parsers: [uppercaseParser],
                props: {
                  label: 'Tax Identification Number (TIN)',
                  placeholder: 'e.g. 1234567A',
                  required: true,
                  maskConfig: { mask: 'A*' },
                },
                expressions: {
                  hide: ({ model }) => model?.[BusinessEntityModelKeys.usCorporation] !== true,
                },
              },
            ],
          },
          {
            key: BusinessEntityModelKeys.participateIndirectly,
            type: '#yesNo',
            props: {
              label:
                'Does your company engage in any activity that deals with persons or entities in the United Nations Sanctions Lists directly or indirectly?',
              required: true,
            },
          },
          {
            key: BusinessEntityModelKeys.usIndicia,
            type: '#yesNo',
            props: {
              label: 'Does your company have US indicia?',
              required: true,
              alwaysShowHint: true,
              tooltip:
                '“US Indicia” refers to:' +
                'Identification as a US citizen or resident' +
                'US registered address or mailing address or post office box' +
                'US telephone number' +
                'Standing instructions to pa amounts from the account to an account maintained in the US' +
                'Current power of attorney or signatory authority granted to a person with a US address' +
                'An "in-care-of" address or "hold mail" address that is the sole address the Foreign Financial Institution (FFI) has identified for your company*',
            },
            expressions: {
              // 'props.disabled': 'formState.isMyInfo',
              'props.hint': ({ model }) => (model?.[BusinessEntityModelKeys.usIndicia] ? 'Please complete the W-8BEN-E form' : null),
            },
          },
        ],
      },
    ];
  }

  private initAddressFormFields(address: AddressType): PortalFieldConfig[] {
    return [
      {
        key: AddressModelKeys.id,
        type: 'input',
        resetOnHide: false,
        hide: true,
        props: { label: 'ADDRESS_ID' },
        // expressions: { 'props.disabled': 'formState.isMyInfo' },
      },
      {
        key: AddressModelKeys.type,
        type: 'button',
        defaultValue: address,
        resetOnHide: false,
        hide: true,
        props: {
          label: 'ADDRESS_TYPE',
          required: true,
          options: ADDRESS_TYPE_OPTIONS,
        },
        // expressions: { 'props.disabled': 'formState.isMyInfo' },
      },
      {
        fieldGroup: [
          {
            template: '<div class="portal-paragraph field-label">Business Registered Address</div>',
          },
          {
            key: AddressModelKeys.subType,
            type: 'button',
            defaultValue: AddressSubType.LOCAL,
            props: {
              label: ADDRESS_TYPE.RESIDENTIAL_ADDRESS,
              required: true,
              options: ADDRESS_SUB_TYPE_OPTIONS,
            },
            expressions: {
              'props.disabled': field => address === AddressType.RESIDENTIAL_ADDRESS && field.options?.formState?.isMyInfo,
            },
          },
        ],
      },
      {
        className: 'col-start-1 sm:col-span-2',
        fieldGroupClassName: 'field-columns',
        expressions: { hide: ({ model }) => model?.[AddressModelKeys.subType] !== AddressSubType.LOCAL },
        fieldGroup: this.addressFormConfigService.initLocalAddressFormFields(address === AddressType.RESIDENTIAL_ADDRESS),
      },
      {
        className: 'col-start-1 sm:col-span-2',
        fieldGroupClassName: 'field-columns',
        expressions: { hide: ({ model }) => model?.[AddressModelKeys.subType] !== AddressSubType.FOREIGN },
        fieldGroup: this.addressFormConfigService.initForeignAddressFormFields(address === AddressType.RESIDENTIAL_ADDRESS),
      },
    ];
  }
}
